<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useDebouncedPatchWorkerKeywords } from "@/modules/worker/composables/useDebouncedPatchWorkerKeywords";

import type { WorkerDetail } from "@/modules/worker/types";

import Badge from "@/dsComponents/Badge/Badge.vue";
import TagInput from "@/dsComponents/inputs/TagInput.vue";

import ProfileSection from "@/components/profile/section/ProfileSection.vue";

const props = withDefaults(
    defineProps<{
        worker: WorkerDetail;
        refreshWorker?: () => Promise<void>;
        readonly: boolean;
        asideLayout?: boolean;
    }>(),
    { asideLayout: false }
);

const { t } = useI18n();

const { schedulePatchWorkerKeywords } = useDebouncedPatchWorkerKeywords({
    workerId: () => props.worker.id,
    worker: () => props.worker,
});

const keywordsSectionTitle = computed(() => (props.asideLayout ? t("page.interim.profile.interview.keywordsSectionTitle") : t("form.keywords")));
const keywordsFieldPlaceholder = computed(() =>
    props.asideLayout ? t("page.interim.profile.interview.keywordsPlaceholder") : t("form.keywordsPlaceholder")
);

const keywords = ref<string[]>(props.worker.keywords ?? []);

watch(
    () => props.worker.keywords,
    (newVal) => {
        keywords.value = newVal ?? [];
    }
);

const saveKeywords = (newKeywords: string[]) => {
    keywords.value = newKeywords;
    schedulePatchWorkerKeywords(newKeywords);
};
</script>
<template>
    <div class="worker-keywords">
        <ProfileSection :title="{ label: keywordsSectionTitle }" :section-gap="asideLayout ? 'var(--spacing-xl, 16px)' : undefined">
            <template #content>
                <TagInput
                    v-if="!readonly"
                    :button-label="t('global.actions.add')"
                    :model-value="keywords"
                    :placeholder="keywordsFieldPlaceholder"
                    add-button-type="neutral"
                    @update:model-value="saveKeywords"
                />
                <div v-else class="worker-keywords__badges">
                    <Badge v-for="keyword in keywords" :key="keyword" :label="keyword" size="xl" color="neutral" />
                </div>
            </template>
        </ProfileSection>
    </div>
</template>
<style scoped lang="scss">
.worker-keywords {
    &__badges {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-lg, 12px);
    }
}
</style>
